import React, { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { useNavigate } from 'react-router-dom';
import {
  db,
  collection,
  doc,
  getDoc,
  setDoc,
  getDocs,
  query,
  orderBy,
} from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import type { User } from '@/types';
import {
  Users,
  Wine,
  TrendingUp,
  ArrowRight,
  FileText,
  Bot,
  Save,
  Loader2,
} from 'lucide-react';
import { motion } from 'framer-motion';

export const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'superadmin';

  const [users, setUsers] = useState<User[]>([]);
  const [wines, setWines] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [geminiModelId, setGeminiModelId] = useState('gemini-2.5-flash');
  const [geminiInput, setGeminiInput] = useState('');
  const [geminiSaving, setGeminiSaving] = useState(false);
  const [geminiError, setGeminiError] = useState('');
  const [geminiSuccess, setGeminiSuccess] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
      const usersSnapshot = await getDocs(usersQuery);
      const usersData = usersSnapshot.docs.map(
        (d) => ({ id: d.id, ...d.data() } as User)
      );
      setUsers(usersData);

      const winesSnapshot = await getDocs(collection(db, 'wines'));
      setWines(winesSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));

      const geminiSnap = await getDoc(doc(db, 'config', 'gemini'));
      if (geminiSnap.exists()) {
        setGeminiModelId(geminiSnap.data().modelId || 'gemini-2.5-flash');
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const extractModelId = (input: string): string | null => {
    const trimmed = input.trim();
    const urlMatch = trimmed.match(/\/models\/([a-zA-Z0-9._-]+)/);
    if (urlMatch) return urlMatch[1];
    const anchorMatch = trimmed.match(/#(gemini-[a-zA-Z0-9._-]+)/);
    if (anchorMatch) return anchorMatch[1];
    if (/^[a-zA-Z][a-zA-Z0-9._-]+$/.test(trimmed) && trimmed.length > 3) return trimmed;
    return null;
  };

  const saveGeminiModel = async () => {
    const modelId = extractModelId(geminiInput);
    if (!modelId) {
      setGeminiError('Formato non valido. Incolla il link al modello o il model ID (es. gemini-2.5-flash).');
      return;
    }
    setGeminiSaving(true);
    setGeminiError('');
    try {
      await setDoc(doc(db, 'config', 'gemini'), { modelId }, { merge: true });
      setGeminiModelId(modelId);
      setGeminiInput('');
      setGeminiSuccess(true);
      setTimeout(() => setGeminiSuccess(false), 3000);
    } catch {
      setGeminiError('Errore nel salvataggio. Verifica i permessi Firestore sulla collection config.');
    } finally {
      setGeminiSaving(false);
    }
  };

  // Stats
  const stats = {
    totalUsers: users.length,
    activeUsers: users.filter((u) => u.status === 'active').length,
    regularUsers: users.filter((u) => u.role === 'user').length,
    admins: users.filter((u) => u.role === 'admin' || u.role === 'superadmin').length,
    totalWines: wines.length,
    totalValue: wines.reduce((sum, w) => sum + (w.estimatedValue || 0), 0),
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard Admin</h1>
          <p className="text-gray-600 mt-1">Panoramica generale del sistema</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 auto-rows-fr">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="h-full">
            <Card className="cursor-pointer hover:shadow-lg transition-shadow h-full" onClick={() => navigate('/admin/users')}>
              <CardContent className="pt-6">
                <div className="flex flex-col lg:flex-row items-center lg:justify-between gap-3">
                  <div className="order-2 lg:order-1 text-center lg:text-left">
                    <p className="text-xs sm:text-sm font-medium text-gray-600">Utenti Totali</p>
                    <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2">{stats.totalUsers}</p>
                    <p className="text-xs sm:text-sm text-green-600 mt-1">
                      {stats.activeUsers} attivi
                    </p>
                  </div>
                  <div className="order-1 lg:order-2 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="h-full">
            <Card className="cursor-pointer hover:shadow-lg transition-shadow h-full" onClick={() => navigate('/admin/users')}>
              <CardContent className="pt-6">
                <div className="flex flex-col lg:flex-row items-center lg:justify-between gap-3">
                  <div className="order-2 lg:order-1 text-center lg:text-left">
                    <p className="text-xs sm:text-sm font-medium text-gray-600">Utenti Standard</p>
                    <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2">{stats.regularUsers}</p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">Con accesso cantina</p>
                  </div>
                  <div className="order-1 lg:order-2 w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="h-full">
            <Card className="h-full">
              <CardContent className="pt-6">
                <div className="flex flex-col lg:flex-row items-center lg:justify-between gap-3">
                  <div className="order-2 lg:order-1 text-center lg:text-left">
                    <p className="text-xs sm:text-sm font-medium text-gray-600">Vini Totali</p>
                    <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2">{stats.totalWines}</p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">In tutte le cantine</p>
                  </div>
                  <div className="order-1 lg:order-2 w-12 h-12 bg-wine-purple/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Wine className="w-6 h-6 text-wine-purple" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="h-full">
            <Card className="h-full">
              <CardContent className="pt-6">
                <div className="flex flex-col lg:flex-row items-center lg:justify-between gap-3">
                  <div className="order-2 lg:order-1 text-center lg:text-left">
                    <p className="text-xs sm:text-sm font-medium text-gray-600">Valore Totale</p>
                    <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2 break-words">
                      €{stats.totalValue.toLocaleString('it-IT')}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">Valore stimato</p>
                  </div>
                  <div className="order-1 lg:order-2 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Card
              className="cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
              onClick={() => navigate('/admin/users')}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Gestione Utenti</CardTitle>
                      <CardDescription>Crea e gestisci utenti del sistema</CardDescription>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                </div>
              </CardHeader>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
            <Card
              className="cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
              onClick={() => navigate('/admin/audit')}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Log Attività</CardTitle>
                      <CardDescription>Storico completo delle azioni</CardDescription>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                </div>
              </CardHeader>
            </Card>
          </motion.div>
        </div>

        {/* AI Configuration — superadmin only */}
        {isSuperAdmin && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <Bot className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">AI Configuration</CardTitle>
                    <CardDescription>Modello Gemini per il riconoscimento vini</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">Modello attivo</p>
                    <p className="font-mono text-sm font-semibold text-gray-900 mt-1">{geminiModelId}</p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Cambia modello</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={geminiInput}
                        onChange={(e) => {
                          setGeminiInput(e.target.value);
                          setGeminiError('');
                          setGeminiSuccess(false);
                        }}
                        placeholder="Incolla link AI Studio o model ID (es. gemini-2.5-flash)"
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
                      />
                      <button
                        onClick={saveGeminiModel}
                        disabled={geminiSaving || !geminiInput.trim()}
                        className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                      >
                        {geminiSaving
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Save className="w-4 h-4" />
                        }
                        Salva
                      </button>
                    </div>
                    {geminiInput.trim() && (() => {
                      const preview = extractModelId(geminiInput);
                      return preview
                        ? <p className="text-xs text-green-600">Model ID estratto: <span className="font-mono font-semibold">{preview}</span></p>
                        : <p className="text-xs text-red-500">Formato non riconosciuto — prova ad incollare direttamente il model ID.</p>;
                    })()}
                    {geminiError && <p className="text-xs text-red-500">{geminiError}</p>}
                    {geminiSuccess && <p className="text-xs text-green-600">Modello aggiornato con successo.</p>}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </AdminLayout>
  );
};
